import { useRef } from "react";
import { toast } from "sonner";
import { useUploadSiteImage } from "@/hooks/useSiteEditor";

const ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * An image of the site: uploaded on pick, then its storage PATH goes into the
 * draft, which saves itself. «Quitar» only clears the path — the file stays in
 * the bucket, because an older publication may still show it and restoring that
 * publication must find it there.
 */
export function SiteImageField({
  label,
  hint,
  path,
  assetBaseUrl,
  maxSizeMb,
  onChange,
  compact,
}: {
  label: string;
  hint?: string;
  path: string | null;
  assetBaseUrl: string;
  /** The server's own cap (`logoMaxSizeMb`), so the check here cannot disagree with it. */
  maxSizeMb: number | undefined;
  onChange: (path: string | null) => void;
  compact?: boolean;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadSiteImage();

  function pick(file: File | undefined): void {
    if (!file) return;
    if (maxSizeMb !== undefined && file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`La imagen supera el límite de ${maxSizeMb} MB`);
      return;
    }
    upload.mutate(file, { onSuccess: (result) => onChange(result.path) });
  }

  const preview = path ? `${assetBaseUrl}${path}` : null;
  return (
    <div className="flex flex-col gap-1.5">
      {!compact && <span className="text-[13px] font-medium text-ink-900">{label}</span>}
      <div className="flex items-center gap-3">
        <div
          className={`${compact ? "h-12 w-12" : "h-16 w-24"} shrink-0 overflow-hidden rounded-md border border-ink-100 bg-ink-050`}
        >
          {preview && <img src={preview} alt="" className="h-full w-full object-contain" />}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={upload.isPending}
            className="font-medium text-moss hover:underline disabled:opacity-50"
            aria-label={path ? `Cambiar ${label.toLowerCase()}` : `Subir ${label.toLowerCase()}`}
          >
            {upload.isPending ? "Subiendo…" : path ? "Cambiar" : "Subir imagen"}
          </button>
          {path && (
            <button
              type="button"
              onClick={() => onChange(null)}
              disabled={upload.isPending}
              className="text-ink-500 hover:underline disabled:opacity-50"
            >
              Quitar
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      {hint && !compact && (
        <span className="text-[12px] text-ink-500">
          {hint} PNG, JPG o WEBP{maxSizeMb !== undefined ? `, hasta ${maxSizeMb} MB` : ""}.
        </span>
      )}
    </div>
  );
}
